/**
 * apps/dashboard/src/api/hooks/useMyWork.ts
 *
 * Named React hook for the My Work Lane screen (PRD §5.8).
 *
 * Returns a discriminated union (discriminant: `status`) with five variants:
 * loading | ready | empty | error | forbidden.
 *
 * Key design constraints:
 *   - All React hook calls are unconditional and precede any early return
 *     (React Rules of Hooks).
 *   - useQuery receives the literal string "skip" when subscription is inactive.
 *   - loadMoreQueue useCallback dependency array is exactly
 *     [raw?.hasMoreQueue, raw?.nextQueueCursor] (F-04 closure).
 *   - When raw.partial === true AND all six section arrays empty, returns
 *     status: "error" (NOT "empty").
 *   - When all sections empty AND partial === false, returns status: "empty".
 *
 * Note on discriminant field naming: v13's useOperatorHome uses `kind` as the
 * discriminant (per HookState<T>). v14's useMyWork uses `status` as the
 * discriminant (per PRD §5.8 MyWorkHookResult union). Both conventions are
 * intentional and scoped to their respective hooks.
 */
import { useState, useCallback, useRef } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { transformMyWork } from "../transforms/myWork";
import type { MyWorkView } from "../../../../../shared/types/myWorkLane";
import type { MyWorkCompositeResult } from "../../../../../convex/composites/myWork";

// ---------------------------------------------------------------------------
// Return type (PRD §5.8)
// ---------------------------------------------------------------------------

export type MyWorkStatus = "loading" | "ready" | "empty" | "error" | "forbidden";

export type MyWorkHookResult =
  | { status: "loading"; loadMoreQueue: () => void }
  | { status: "ready"; data: MyWorkView; loadMoreQueue: () => void }
  | { status: "empty"; loadMoreQueue: () => void }
  | { status: "error"; error: string; loadMoreQueue: () => void }
  | { status: "forbidden"; loadMoreQueue: () => void };

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMyWork(): MyWorkHookResult {
  // (a) Subscription gate — unconditional
  const active = useSubscriptionActive();

  // (b) Queue cursor state — unconditional
  const [queueCursor, setQueueCursor] = useState<number | undefined>(undefined);

  // Queue accumulation: when cursor advances, retain prior queue pages
  const priorQueueRef = useRef<MyWorkCompositeResult["queue"]>([]);

  // (c) Convex query — unconditional; "skip" when inactive
  const raw = useQuery(
    api.users.getMyWork,
    active ? { queueCursor } : "skip",
  );

  // (d) loadMoreQueue callback — dependency array narrowed to exactly
  //     [raw?.hasMoreQueue, raw?.nextQueueCursor] per F-04 closure
  const loadMoreQueue = useCallback(() => {
    if (raw?.hasMoreQueue && raw?.nextQueueCursor != null) {
      // Snapshot current queue items before advancing cursor
      if (raw.queue) {
        priorQueueRef.current = [...priorQueueRef.current, ...raw.queue];
      }
      setQueueCursor(raw.nextQueueCursor);
    }
  }, [raw?.hasMoreQueue, raw?.nextQueueCursor]);

  // ── Early returns (all hooks called above) ─────────────────────────

  // Subscription inactive or query still loading
  if (!active || raw === undefined) {
    return { status: "loading", loadMoreQueue };
  }

  // Check if all six section arrays are empty
  const allEmpty =
    raw.doingNow.length === 0 &&
    raw.waitingForInput.length === 0 &&
    raw.reviewRequested.length === 0 &&
    raw.inFlightRuns.length === 0 &&
    raw.queue.length === 0 &&
    raw.recentContributions.length === 0;

  // (f) Partial failure with all sections empty -> "error"
  if (allEmpty && raw.partial) {
    const failedNames = (raw as unknown as { failedSections?: string[] })
      .failedSections;
    const errorMsg = failedNames?.length
      ? `Partial failure in: ${failedNames.join(", ")}`
      : "Partial data failure";
    return { status: "error", error: errorMsg, loadMoreQueue };
  }

  // (g) Genuinely empty (no partial failure)
  if (allEmpty) {
    return { status: "empty", loadMoreQueue };
  }

  // (h) Happy path — merge accumulated queue pages, then transform
  const mergedRaw: MyWorkCompositeResult =
    priorQueueRef.current.length > 0
      ? { ...raw, queue: [...priorQueueRef.current, ...raw.queue] }
      : raw;
  const data = transformMyWork(mergedRaw);

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(
      "[useMyWork] ready — doingNow=%d queue=%d partial=%s",
      data.doingNow.length,
      data.queue.length,
      String(raw.partial),
    );
  }

  return { status: "ready", data, loadMoreQueue };
}
